// @flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory, useParams} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Hidden,
  IconButton,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import {ArrowBack, DeleteOutline, LocalOfferOutlined} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import SearchBar from '../../../elements/SearchBar/SearchBar';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import HorizontalFilter from '../../../elements/HorizontalFilter/HorizontalFilter';
import HorizontalScroll from '../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from '../../../elements/HorizontalFilter/FilterButton/FilterButton';
import Quantity from '../../../elements/Quantity/Quantity';
import ProductConfigurationModal from '../../manual-order/components/ProductConfigurationModal';
import {getProductItemOriginalPrice} from '../../../utils/product';
import {useIsFeatureInTier} from '../../../utils/component';
import {getImageUrl} from '../../../utils/image_provider';
import {usePage} from '../../page/utils';
import {commaFormat} from '../../../utils/number';

import {getProductTaxFactor} from '../../products/utils';
import {useTypedSelector} from '../../../store';

import ProductItem from '../../manual-order/components/ProductItem';
import ShoppingCartFooter from '../../manual-order/components/ShoppingCartFooter';

import type {
  Store,
  Product,
  Currency,
  ProductVariant,
  Category,
  Pagination,
  Order,
  CartSummaryItem,
} from '../../../entities';
import type {
  ShoppingCart,
  ManualOrderAction,
  ShoppingCartItem,
  ShoppingCartPricesRequest,
} from '../duck';
import type {CheckoutRequest} from '../../manual-order/api';
import {useDebounceEffect, useNearScreen} from '../../../utils/react';
import {useSearchProductEnabled} from '../../products/hooks/useSearchProductEnabled';

import type {OrderItemProductOption} from '../../../entities';
import {buildCartItemID} from '../../manual-order/utils/build-cart-item-id';

type Props = {
  store?: Store,
  currency: Currency,
  categories: Category[],
  fetchProducts: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  products?: Product[],
  isFetchingProducts?: boolean,
  pagination?: Pagination,
  fetchShoppingCartPrices: (payload: ManualOrderAction) => void,
  applyCoupon: (payload: ManualOrderAction) => void,
  removeCoupon: (payload?: ManualOrderAction) => void,
  addGlobalDiscountShoppingCart: (payload: ManualOrderAction) => void,
  removeGlobalDiscountShoppingCart: (payload: ManualOrderAction) => void,
  itemPrices?: CartSummaryItem[],
  subTotal?: string,
  editOrder: ({checkoutRequest: CheckoutRequest}) => void,
  editOrderSuccess?: boolean,
  editOrderError?: string,
  isEditingOrder?: boolean,
  order?: Order,
  couponDiscount?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 0, 2),
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  alert: {
    maxWidth: 1300,
    margin: theme.spacing(1, 4, 0, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(1, 2, 0, 2),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    maxWidth: 1300,
    padding: theme.spacing(0, 4, 4, 4),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 64,
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(0, 2, 2, 2),
      marginBottom: 180,
    },
  },
  orderLeftContent: {
    marginTop: theme.spacing(2),
    flex: '1 2 65%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    background: 'white',
  },
  orderRightContent: {
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
    marginLeft: theme.spacing(4),
    flex: '1 2 35%',
    marginTop: theme.spacing(2),
    position: 'sticky',
    top: 100,
  },
  orderContent: {
    // marginBottom: theme.spacing(2),
    minWidth: 320,
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: colors.grey900,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  categoriesContainer: {
    margin: theme.spacing(0, -3, 1, -3),
  },
  loadingSpinner: {
    alignSelf: 'center',
    marginTop: theme.spacing(3),
  },
  productList: {
    margin: theme.spacing(2, 0),
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    },
    [theme.breakpoints.up('lg')]: {
      gap: '16px',
    },
  },
  product: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    // alignItems: 'center',
    position: 'relative',
  },
  disabledProduct: {
    opacity: 0.5,
    filter: 'grayscale(50%)',
  },
  productImage: {
    borderRadius: '12px',
    height: 104,
    width: 104,
    marginRight: 24,
  },
  productDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    marginRight: 24,
  },
  productName: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: 110,
    },
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  productVariants: {
    borderRadius: '4px',
    backgroundColor: colors.grey200,
    padding: '2px 8px',
    color: colors.grey700,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  productPrice: {
    display: 'flex',
  },
  productPriceNormal: {
    fontWeight: 500,
    fontSize: '1.125rem',
    whiteSpace: 'nowrap',
  },
  productPriceSlashed: {
    marginLeft: theme.spacing(1),
    fontSize: '1.125rem',
    whiteSpace: 'nowrap',
    color: colors.grey500,
    textDecoration: 'line-through',
  },
  productBulkPriceFlag: {
    position: 'absolute',
    top: 0,
    right: 8,
    backgroundColor: '#158CFD',
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '24px',
    width: 'fit-content',
    padding: '2px 8px',
    [theme.breakpoints.down('xs')]: {
      right: 6,
      padding: 6,
      color: '#158CFD',
      background: '#E9F4FF',
    },
  },
  productNoStockFlag: {
    position: 'absolute',
    top: 0,
    right: 8,
    backgroundColor: colors.grey200,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '24px',
    width: 'fit-content',
    padding: '4px 12px',
  },
  productAddButton: {
    alignSelf: 'center',
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      alignSelf: 'flex-end',
    },
  },
  shoppingCartItemsAmount: {
    marginLeft: 'auto',
    backgroundColor: colors.grey200,
    borderRadius: '12px',
    padding: '4px 12px',
    fontWeight: 600,
  },
  shoppingCartItems: {
    margin: theme.spacing(0, -3),
    padding: theme.spacing(0, 3),
    maxHeight: 'calc(100vh - 376px)',
    overflow: 'auto',
  },
  shoppingCartProductDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    marginRight: -36,
  },
  shoppingCartProductImage: {
    borderRadius: '12px',
    height: 64,
    width: 64,
    marginRight: 16,
  },
  shoppingCartProductName: {
    fontSize: '0.875rem',
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  shoppingCartProductVariant: {
    fontSize: '0.875rem',
    color: colors.grey700,
    fontWeight: 500,
  },
  shoppingCartProductOptions: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
  },
  quantity: {
    marginTop: theme.spacing(1),
  },
  shoppingCartItemActions: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  shoppingCartItemDeleteButton: {
    color: colors.error,
  },
  shoppingCartProductBulkPriceFlag: {
    padding: 6,
    color: '#158CFD',
    background: '#E9F4FF',
    marginRight: 8,
    display: 'flex',
    borderRadius: '20px',
  },
  shoppingCartProductPrice: {
    textAlign: 'right',
  },
  shoppingCartProductPriceNormal: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    color: colors.grey800,
    whiteSpace: 'nowrap',
  },
  shoppingCartProductPriceDiscounted: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey600,
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
  shoppingCartFooter: {
    marginTop: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  shoppingCartSubtotal: {
    marginTop: theme.spacing(1),
    color: colors.grey800,
    fontWeight: 400,
  },
  shoppingCartTotal: {
    marginTop: theme.spacing(2),
    color: colors.grey800,
    fontWeight: 600,
  },
  checkoutButton: {
    width: '100%',
    height: 48,
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  successContent: {
    marginTop: theme.spacing(2),
    width: 480,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    background: 'white',
  },
  successIcon: {
    marginTop: theme.spacing(2),
    color: colors.success,
  },
  successTitle: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontWeight: 600,
  },
  successMessage: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  successButton: {
    marginTop: theme.spacing(4),
    width: 240,
  },
  successReturnButton: {
    margin: theme.spacing(1, 0),
  },
  summaryOptions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  summaryOptionText: {
    color: colors.grey700,
    fontSize: '14px',
  },
  loadingSpinnerWrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default usePage<Props>(EditOrderItems, 'Edit Order Items');

function EditOrderItems(props: Props) {
  const {
    store,
    currency,
    categories,
    products,
    isFetchingProducts,
    pagination,
    fetchProducts,
    fetchShoppingCartPrices,
    itemPrices,
    // subTotal,
    editOrder,
    editOrderSuccess,
    editOrderError,
    order,
    applyCoupon,
    removeCoupon,
    isEditingOrder,
    removeGlobalDiscountShoppingCart,
    addGlobalDiscountShoppingCart,
    couponDiscount,
  } = props;

  const classes = useStyles();

  const history = useHistory();
  const {orderId} = useParams();

  const {fromRef: nearScreenRef, isNearScreen} = useNearScreen({
    distance: '400px',
    once: false,
  });

  const promotionsApplied = useTypedSelector(
    state => state.manualOrder.promotions
  );
  const scGlobalDiscount = useTypedSelector(
    state => state.manualOrder?.shoppingCart?.globalDiscount
  );
  const scCoupon = useTypedSelector(
    state => state.manualOrder?.shoppingCart?.couponCode
  );

  const coupon = useTypedSelector(state => state.manualOrder.coupon);
  const subtotal = useTypedSelector(state => state.manualOrder.subTotal);
  const globalDiscountData = useTypedSelector(
    state => state.manualOrder.globalDiscountData
  );

  const hasOrderEditingFeature = useIsFeatureInTier('ORDER_EDIT_ITEMS');

  // const subTotal = useTypedSelector(state => state.manualOrder.subTotal);
  const total = useTypedSelector(state => state.manualOrder.total);

  useEffect(() => {
    if (!hasOrderEditingFeature && orderId)
      history.replace(`/orders/${orderId}`);
    // return () => resetState();
  }, [hasOrderEditingFeature, history, orderId]);

  const [stockError, setStockError] = useState<string | typeof undefined>();

  useEffect(() => {
    if (!itemPrices || !order || !order.items) return;

    const itemError = itemPrices.find(item => item.item.error);
    if (!itemError) return setStockError();

    const product = order.items.find(
      item => item.productId === itemError.item.product
    );

    if (!product) return setStockError();

    if (itemError.item.error === 'OUT_OF_STOCK') {
      return setStockError(`El producto ${product.name} no tiene stock`);
    }

    setStockError('Error al calcular el precio');
  }, [itemPrices, order]);

  const itemPricesError = itemPrices?.filter(el => el.item.error);

  const getSummaryProductError = (i: ShoppingCartItem) => {
    if (i && itemPricesError && itemPricesError.length) {
      const index = itemPricesError.findIndex(price =>
        i.productVariant
          ? price.item.productVariant === i.productVariant.id
          : price.item.product === i.product.id
      );
      if (index >= 0 && itemPricesError[index]) return itemPricesError[index];
    }
  };

  const [shoppingCart, setShoppingCart] = useState<ShoppingCart>({items: []});

  const addShoppingCartItem = ({
    product,
    productVariant,
    amount,
    productOptions,
  }: {
    product: Product,
    productVariant?: ProductVariant,
    amount: number,
    productOptions?: OrderItemProductOption[],
  }) => {
    setShoppingCart((prev: ShoppingCart): ShoppingCart => {
      const shoppingCart = {...prev};

      const itemId = buildCartItemID(
        product.id,
        productVariant?.id,
        productOptions?.map(option => ({
          optionId: option.productOptionId,
          optionGroupId: option.productOptionGroupId,
        }))
      );
      const foundIndex = shoppingCart.items.findIndex(i => i.id === itemId);
      if (foundIndex === -1) {
        shoppingCart.items.push({
          product,
          productVariant,
          amount,
          productOptions,
        });
      } else {
        shoppingCart.items[foundIndex].amount += parseInt(amount);
        shoppingCart.items[foundIndex].productOptions = productOptions;
      }
      console.log({shoppingCart});
      return shoppingCart;
    });
  };
  const deleteShoppingCartItem = ({itemId}: {item: string}) => {
    setShoppingCart((prev: ShoppingCart): ShoppingCart => {
      const shoppingCart = {...prev};

      const foundIndex = shoppingCart.items.findIndex(i => i.id === itemId);
      shoppingCart.items.splice(foundIndex, 1);
      return shoppingCart;
    });
  };

  useEffect(() => {
    if (order && order.items && order.items.length) {
      // INITIALIZE SHOPPING CART
      const items = order.items.map(item => {
        const itemId = buildCartItemID(
          item.productId,
          item?.productVariantId,
          item?.detail?.productOptions?.map(po => ({
            optionId: po.id,
            optionGroupId: po.productOptionGroupId,
          }))
        );

        return {
          id: itemId,
          orderItemId: item.id,
          product: {
            id: item.productId,
            price: item.price,
            mainImage: {
              id: item.imageId,
            },
            name: item.name,
          },
          productVariant: item.productVariantId
            ? {
                id: item.productVariantId,
                price: item.price,
                imageId: item.imageId,
                options: item.productVariant,
              }
            : undefined,
          amount: item.quantity,
          productOptions: item.detail?.productOptions?.map(po => {
            return {
              productOptionId: po.id,
              productOptionGroupId: po.productOptionGroupId,
              quantity: po.quantity,
              text: po.text || undefined,
            };
          }),
        };
      });
      setShoppingCart({
        items,
      });
    }
  }, [order]);

  const [categoriesSelected, setCategoriesSelected] = useState([]);
  // const [page, setPage] = useState(1);
  // const [size, setSize] = useState(16);
  // const [filter, setFilter] = useState('');
  // const [filterObject] = useState<{
  //   enabled?: number,
  //   order?: string,
  //   sort?: string,
  //   stock?: number,
  // }>({});

  const [savedSearchString, setSavedSearchString] = useState('');

  const buildQueryParamsCallback = useCallback(
    (searchString?: string): {[key: string]: any} => {
      const params: {[key: string]: any} = {
        // order: filterObject.order,
        // sort: filterObject.sort,
        enabled: 1,
        // stock: filterObject.stock,
        // page: page - 1,
        // size: size,
        size: 32,
      };
      if (searchString) {
        setSavedSearchString(searchString);
        params.name = searchString;
      }
      if (categoriesSelected.length > 0) {
        params.categories = categoriesSelected.toString();
      }
      return params;
    },
    [categoriesSelected]
  );

  const fetchProductsCallback = useCallback(
    (searchString?: string) => {
      const queryFilter = buildQueryParamsCallback(searchString);
      if (queryFilter) {
        fetchProducts({queryFilter});
      }
    },
    [buildQueryParamsCallback, fetchProducts]
  );

  const handleCategoryOption = useCallback(
    (id: string) => {
      categoriesSelected.indexOf(id) === -1
        ? setCategoriesSelected([...categoriesSelected, id])
        : setCategoriesSelected(categoriesSelected.filter(e => e !== id));
    },
    [categoriesSelected]
  );

  const handleAddPage = useCallback(() => {
    if (
      !isFetchingProducts &&
      products &&
      pagination &&
      pagination.total > pagination.size &&
      products.length < pagination.total
    ) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);
      if (queryFilter) {
        fetchProducts({queryFilter, addPage: true});
      }
    }
  }, [
    buildQueryParamsCallback,
    fetchProducts,
    isFetchingProducts,
    pagination,
    products,
    savedSearchString,
  ]);

  useEffect(() => {
    fetchProductsCallback();
  }, [fetchProductsCallback]);

  useDebounceEffect(
    () => {
      if (!isNearScreen) return;
      handleAddPage();
    },
    500,
    [handleAddPage, isNearScreen]
  );

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [
    productConfiguratorModalOpen,
    setProductConfiguratorModalOpen,
  ] = useState(false);

  const handleAddToCartFromConfigurationModal = (
    product: Product,
    options: {
      productVariant?: ProductVariant,
      productOptions?: OrderItemProductOption[],
      quantity: number,
    }
  ) => {
    const {productVariant, productOptions, quantity} = options;

    addShoppingCartItem({
      product,
      productVariant: productVariant,
      amount: quantity,
      productOptions: productOptions,
    });
    setSelectedProduct(null);
    setProductConfiguratorModalOpen(false);
  };

  const handleModalConfigurationClose = () => {
    setSelectedProduct(null);
    setProductConfiguratorModalOpen(false);
  };

  const handleAddProductToCart = (product, amount) => {
    const hasVariants =
      product.productVariantSetting?.enabled &&
      product.productVariantSetting.variants &&
      product.productVariantSetting.variants.length > 0;

    const hasOptions =
      product.productOptionSetting?.enabled &&
      product.productOptionSetting.groups &&
      product.productOptionSetting.groups.length > 0;
    console.log({hasVariants, hasOptions, product});

    if (hasVariants || hasOptions) {
      setSelectedProduct(product);
      setProductConfiguratorModalOpen(true);
    } else {
      addShoppingCartItem({
        product,
        amount: amount,
      });
    }
  };
  const handleDeleteProductFromCart = (itemId: string) => {
    deleteShoppingCartItem({itemId});
  };

  const buildShoppingCartPricesRequest = useCallback(() => {
    if (store) {
      const shoppingCartPricesRequest: ShoppingCartPricesRequest = {
        items: [],
        globalDiscount: scGlobalDiscount,
        coupon: scCoupon,
        orderId: orderId,
      };
      if (shoppingCart && shoppingCart.items) {
        shoppingCart.items.forEach(i =>
          shoppingCartPricesRequest.items.push({
            productId: i.product.id,
            productVariantId: i.productVariant
              ? i.productVariant.id
              : undefined,
            quantity: i.amount,
            productOptions: i.productOptions,
          })
        );
      }
      if (shoppingCart && shoppingCart.couponCode) {
        shoppingCartPricesRequest.coupon = shoppingCart.couponCode;
      }

      if (order?.customer?.phone) {
        shoppingCartPricesRequest.customer = {
          phone: order?.customer?.phone,
        };
      }
      return shoppingCartPricesRequest;
    }
    return {};
  }, [order, orderId, scCoupon, scGlobalDiscount, shoppingCart, store]);

  useEffect(() => {
    if (store && shoppingCart) {
      const shoppingCartPricesRequest: ShoppingCartPricesRequest = buildShoppingCartPricesRequest();
      fetchShoppingCartPrices({
        shoppingCartPricesRequest: shoppingCartPricesRequest,
      });
    }
  }, [
    buildShoppingCartPricesRequest,
    fetchShoppingCartPrices,
    shoppingCart,
    store,
  ]);

  const handleSaveOrder = () => {
    if (shoppingCart && shoppingCart.items && order) {
      const items = shoppingCart.items.map(
        (item: ShoppingCartItem, i: number) => {
          // if (itemPrices[i].error) return [];
          return {
            productId: item.product.id ?? undefined,
            productVariantId: item.productVariant
              ? item.productVariant.id
              : undefined,
            quantity: item.amount,
            // comment: item.comment ? item.comment : undefined,
          };
        }
      );
      const request = {
        orderData: {
          items,
          coupon: scCoupon || undefined,
          globalDiscount: scGlobalDiscount || undefined,
        },
      };
      // console.log(request);
      editOrder({checkoutRequest: {id: order.id, request}});
    }
  };

  useEffect(() => {
    if (editOrderSuccess && order) history.push(`/orders/${order.id}`);
  }, [editOrderSuccess, history, order, orderId]);

  const shoppingCartItems =
    shoppingCart && shoppingCart.items && shoppingCart.items.length ? (
      <div className={classes.shoppingCartItems}>
        {shoppingCart.items.map((item: ShoppingCartItem, i) => {
          const productError = getSummaryProductError(item);
          console.log({
            item,
          });
          let taxFactor = 1;
          let currentStock = 0;
          if (item.product.stockSetting && item.product.stockSetting.enabled) {
            if (
              item.productVariant &&
              typeof item.productVariant.stock === 'number'
            )
              currentStock = item.productVariant.stock;
            else currentStock = item.product.stockSetting.stock;
          }

          let productPrice = {unitPrice: getProductItemOriginalPrice(item)};
          const productOptionsNames = [];

          if (item && itemPrices && itemPrices.length) {
            const index = itemPrices.findIndex(price =>
              item.productVariant
                ? price.item.productVariant === item.productVariant.id
                : price.item.product === item.product.id
            );
            const draftItem = itemPrices[index];
            if (index >= 0 && draftItem.item.price) {
              productPrice = draftItem.item.price;
              taxFactor = getProductTaxFactor(draftItem.taxes ?? []);
            }

            if (draftItem?.item?.detail?.productOptions) {
              for (const option of draftItem.item.detail.productOptions) {
                productOptionsNames.push(option.name);
              }
            }
          }
          let productImageId = '';

          if (item.productVariant && item.productVariant.imageId) {
            productImageId = item.productVariant.imageId;
          }
          if (item.product.mainImage && item.product.mainImage.id) {
            productImageId = item.product.mainImage.id;
          }

          if (item.product && item.product.mainImageId) {
            productImageId = item.product.mainImageId;
          }

          const imageUrl = getImageUrl(productImageId);

          return (
            <React.Fragment key={`item-${i}`}>
              <div
                className={`${classes.product}`}
                style={{opacity: productError ? '0.5' : undefined}}
              >
                <img
                  className={classes.shoppingCartProductImage}
                  src={imageUrl}
                  alt=""
                />
                <div className={classes.shoppingCartProductDetail}>
                  <Typography className={classes.shoppingCartProductName}>
                    {item.product.name}
                  </Typography>
                  {item.productVariant && item.productVariant.options && (
                    <Typography className={classes.shoppingCartProductVariant}>
                      {Object.values(item.productVariant.options).join('/')}
                    </Typography>
                  )}
                  {item.productVariant && item.productVariant.combinations && (
                    <Typography className={classes.shoppingCartProductVariant}>
                      {Object.values(item.productVariant.combinations)
                        .join('/')
                        .toLowerCase()}
                    </Typography>
                  )}
                  {productOptionsNames.length > 0 && (
                    <Typography className={classes.shoppingCartProductOptions}>
                      {'• '}
                      {productOptionsNames.join(' • ')}
                    </Typography>
                  )}
                  {/* <div className={classes.quantity}> */}
                  {productError ? (
                    <Typography
                      className={classes.shoppingCartProductName}
                      style={{fontWeight: 700}}
                    >
                      Producto no disponible
                    </Typography>
                  ) : (
                    <Quantity
                      value={item.amount}
                      maxValue={
                        item.product.stockSetting &&
                        item.product.stockSetting.enabled &&
                        currentStock > 0
                          ? currentStock
                          : undefined
                      }
                      type={1}
                      setQuantity={quantity =>
                        addShoppingCartItem({
                          product: item.product,
                          amount: quantity - item.amount,
                          productOptions: item.productOptions,
                        })
                      }
                      addHandler={() =>
                        addShoppingCartItem({
                          product: item.product,
                          productVariant: item.productVariant
                            ? item.productVariant
                            : undefined,
                          amount: 1,

                          productOptions: item.productOptions,
                        })
                      }
                      removeHandler={() =>
                        addShoppingCartItem({
                          product: item.product,
                          productVariant: item.productVariant
                            ? item.productVariant
                            : undefined,
                          amount: -1,
                          productOptions: item.productOptions,
                        })
                      }
                    />
                  )}
                  {/* </div> */}
                </div>
                <div className={classes.shoppingCartItemActions}>
                  <IconButton
                    className={classes.shoppingCartItemDeleteButton}
                    size="small"
                    onClick={() => handleDeleteProductFromCart(item.id)}
                  >
                    <DeleteOutline />
                  </IconButton>
                  <div className={classes.shoppingCartProductPrice}>
                    {parseFloat(productPrice.unitPrice) <
                      (item.productVariant
                        ? parseFloat(item.productVariant.price)
                        : parseFloat(item.product.price)) && (
                      <Typography
                        className={classes.shoppingCartProductPriceDiscounted}
                      >
                        {commaFormat(
                          taxFactor *
                            (item.productVariant
                              ? parseFloat(item.productVariant.price)
                              : parseFloat(item.product.price)) *
                            item.amount,
                          currency ? currency.symbol : ''
                        )}
                      </Typography>
                    )}
                    <Typography
                      className={classes.shoppingCartProductPriceNormal}
                    >
                      {productPrice.source === 'BULK_PRICE' && (
                        <div
                          className={classes.shoppingCartProductBulkPriceFlag}
                        >
                          <LocalOfferOutlined fontSize="small" />
                        </div>
                      )}
                      {!productError &&
                        productPrice &&
                        item.amount &&
                        commaFormat(
                          parseFloat(productPrice.unitPrice) *
                            item.amount *
                            taxFactor,
                          currency ? currency.symbol : ''
                        )}
                    </Typography>
                  </div>
                </div>
              </div>
              {i + 1 < shoppingCart.items.length && (
                <Divider className={classes.divider} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    ) : null;

  const shoppingCartItemsAmount = useMemo(() => {
    if (shoppingCart && shoppingCart.items) {
      let result = 0;
      shoppingCart.items.forEach(i => {
        result += i.amount;
      });
      return result;
    }
  }, [shoppingCart]);

  const productOutOfStock = (product: Product) => {
    if (product.stockSetting && product.stockSetting.enabled) {
      if (
        product.productVariantSetting?.enabled &&
        product.productVariantSetting.variants &&
        product.productVariantSetting.variants.length &&
        product.productVariantSetting.variants.filter(v => v.enabled).length
      ) {
        const variants = product.productVariantSetting.variants;
        const enabledVariants = variants.filter(v => v.enabled);
        let flag = false;
        for (let productV of enabledVariants) {
          if (productV.stock && productV.stock > 0) flag = true;
        }
        return !flag;
      }

      const inShoppingCart = shoppingCart.items.find(item => {
        return item.product.id === product.id;
      });

      const amountInShoppingCart = inShoppingCart ? inShoppingCart.amount : 0;

      // $FlowFixMe
      return product.stockSetting.stock - amountInShoppingCart <= 0;
    }
    return false;
  };

  const handleCouponApply = (couponCode: string) => {
    applyCoupon({
      couponCode,
    });
  };

  const handleCouponRemove = () => {
    removeCoupon();
  };

  const handleGlobalDiscount = discountData => {
    const discountValue = String(discountData.value);

    const discount =
      discountData.type === 'percentage'
        ? {percentageDiscount: discountValue}
        : {fixedDiscount: discountValue};

    addGlobalDiscountShoppingCart({
      globalDiscount: discount,
    });
  };

  useEffect(() => {
    if (order?.invoice?.detail?.discount) {
      const {
        coupon_discount: couponDiscount,
        global_discount: globalDiscount,
      } = order.invoice.detail.discount;

      if (couponDiscount) {
        applyCoupon({couponCode: couponDiscount.code});
      }
      if (globalDiscount) {
        let discountValue = globalDiscount.discount;
        const data = {
          type: globalDiscount.description,
          value: String(Math.abs(parseFloat(discountValue))),
        };
        handleGlobalDiscount(data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addGlobalDiscountShoppingCart, order]);

  const searchProductEnabled = useSearchProductEnabled();

  return (
    <div className={classes.root}>
      <SnackBar mode="error" message={editOrderError} />
      <div className={classes.headerWrapper}>
        <Typography className={classes.title}>
          {orderId && (
            <IconButton
              className={classes.backButton}
              onClick={() => history.push(`/orders/${orderId}`)}
            >
              <ArrowBack />
            </IconButton>
          )}
          Editar Pedido
        </Typography>
      </div>
      <main className={classes.content}>
        <div className={classes.orderLeftContent}>
          <div className={classes.sectionTitleWrapper}>
            <Typography className={classes.sectionTitle}>
              Agrega productos al pedido manual.
            </Typography>
          </div>
          <Divider className={classes.divider} style={{marginBottom: 8}} />
          {categories && categories.length ? (
            <div className={classes.categoriesContainer}>
              <HorizontalFilter>
                <HorizontalScroll>
                  <div style={{width: '24px', minWidth: '24px'}} />
                  {categories.map((c: Category, index: number) => (
                    <FilterButton
                      key={`category-${index}`}
                      // selected={c.id === categorySelected}
                      selected={categoriesSelected.indexOf(c.id) !== -1}
                      id={c.id}
                      handleOnClick={handleCategoryOption}
                      text={c.name}
                    />
                  ))}
                </HorizontalScroll>
              </HorizontalFilter>
            </div>
          ) : null}

          <SearchBar
            placeholder="Buscar producto"
            color="light"
            externalHandleSearch={fetchProductsCallback}
            disabled={!searchProductEnabled}
          />
          <div className={classes.productList}>
            {products && products.length ? (
              <>
                {products
                  // .filter(p => p.enabled)
                  .map((product, i) => {
                    const isInPromotion =
                      product &&
                      product.promotions &&
                      product.promotions.length;

                    const outOfStock = productOutOfStock(product);

                    return (
                      <ProductItem
                        key={`edit-order-item-${product.id}`}
                        currency={currency}
                        product={product}
                        isInPromotion={!!isInPromotion}
                        outOfStock={outOfStock}
                        onAdd={() => handleAddProductToCart(product, 1)}
                      />
                    );
                  })}
                {isFetchingProducts ? (
                  <div className={classes.loadingSpinnerWrap}>
                    <CircularProgress className={classes.loadingSpinner} />
                  </div>
                ) : null}
              </>
            ) : (
              !isFetchingProducts && (
                <div style={{margin: '36px auto'}}>
                  No se encontraron productos
                </div>
              )
            )}

            <div ref={nearScreenRef}></div>
          </div>

          {isFetchingProducts && !(products?.length > 0) && (
            <div className={classes.loadingSpinnerWrap}>
              <CircularProgress className={classes.loadingSpinner} />
            </div>
          )}
        </div>
        <Hidden xsDown>
          <div className={classes.orderRightContent}>
            <div className={classes.orderContent}>
              <div className={classes.sectionTitleWrapper}>
                <Typography className={classes.sectionTitle}>
                  Resumen de Productos
                </Typography>
                <div className={classes.shoppingCartItemsAmount}>
                  {shoppingCartItemsAmount}
                </div>
              </div>
              <Divider className={classes.divider} />
              {shoppingCartItems ? (
                <>
                  {shoppingCartItems}

                  <ShoppingCartFooter
                    couponDiscount={couponDiscount}
                    coupon={coupon}
                    currency={currency}
                    promotionsApplied={promotionsApplied}
                    shoppingCart={shoppingCart}
                    subTotal={subtotal}
                    total={total}
                    handleGlobalDiscount={handleGlobalDiscount}
                    handleCouponApply={handleCouponApply}
                    handleCouponRemove={handleCouponRemove}
                    globalDiscount={globalDiscountData}
                    handleRemoveGlobalDiscount={
                      removeGlobalDiscountShoppingCart
                    }
                  />
                </>
              ) : (
                <div style={{margin: '36px auto'}}>
                  El carrito de compras está vacío
                </div>
              )}
            </div>
            {shoppingCartItems && (
              <div className={classes.orderContent} style={{marginTop: 16}}>
                <CustomButton
                  color="primary"
                  variant="contained"
                  onClick={handleSaveOrder}
                  disabled={!total || !!stockError || isEditingOrder}
                  className={classes.checkoutButton}
                >
                  Continuar
                </CustomButton>
              </div>
            )}
          </div>
        </Hidden>
      </main>
      {selectedProduct && (
        <ProductConfigurationModal
          open={productConfiguratorModalOpen}
          onAddToCart={handleAddToCartFromConfigurationModal}
          onClose={handleModalConfigurationClose}
          product={selectedProduct}
        />
      )}
      <SnackBar mode="error" message={stockError} />
    </div>
  );
}

EditOrderItems.propTypes = {
  store: PropTypes.object,
  currency: PropTypes.object,
  categories: PropTypes.array,
  fetchProducts: PropTypes.func.isRequired,
  products: PropTypes.array,
  isFetchingProducts: PropTypes.bool,
  pagination: PropTypes.object,
  fetchShoppingCartPrices: PropTypes.func.isRequired,
  itemPrices: PropTypes.array,
  subTotal: PropTypes.string,
  editOrder: PropTypes.func,
  editOrderSuccess: PropTypes.bool,
  editOrderError: PropTypes.string,
  order: PropTypes.object,
};
